// My Tasks: all care-plan tasks across the unit with status filter + progress.
// Recording a task opens its fulfillment form (TaskFulfillmentModal) and
// persists through fulfill_care_plan_task/, so what the nurse actually did
// (dose given, status, reaction…) is captured — not just a done flag. Tasks are
// read from nurse_dashboard_tasks/, which inlines each task's order + form.
// Follows the dashboard's date range (falling back to today), like every other
// view — it used to keep a local date picker, so widening the range everywhere
// else left this list showing a different day's work.
import React, { useEffect, useMemo, useState } from "react";
import { Box, Chip, LinearProgress, Paper, Stack, Typography, TextField, MenuItem } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

import { useNurseData } from "../NurseDataContext";
import { useMyCoverage, coveredAdmissionIds, isTaskActionable, useFulfillmentNames } from "../hooks";
import { NURSE_COLORS } from "../theme";
import { getNurseTasks, listOf } from "../api/nurseApi";
import TaskFulfillmentModal from "../components/TaskFulfillmentModal";
import MedicationAdministrationSheet from "../components/MedicationAdministrationSheet";
import { isMedicationTask } from "../medicationDisplay";
import { taskHeading } from "../taskDisplay";

function todayStr() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default function MyTasks() {
  const { patients, currentNurseId, dateFrom, dateTo } = useNurseData();
  const { myBedCodes } = useMyCoverage();
  const fulfillmentNames = useFulfillmentNames();
  const [tasks, setTasks] = useState([]);
  const [statusF, setStatusF] = useState("all");
  const [typeF, setTypeF] = useState("all");
  const [active, setActive] = useState(null); // task being recorded

  // nurse_dashboard_tasks/ accepts only `date` (a single day) and ignores
  // from_date/to_date (nurse_dashboard_tasks.py:43-59), so the range has to be
  // applied in the browser. Fetch unbounded and window below — the same thing
  // DashboardHome and TasksTab do, which is what makes all three agree.
  const reload = () => {
    getNurseTasks({}).then((r) => setTasks(listOf(r))).catch(() => setTasks([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, []);

  const patientByAdmission = useMemo(() => {
    const map = {};
    patients.forEach((p) => { map[String(p.admissionId)] = p; });
    return map;
  }, [patients]);

  // "My" tasks = tasks for the patients I cover this shift (roster ward ∪
  // assigned beds). nurse_dashboard_tasks/ has no server-side nurse scope, so
  // narrow it here; null coverage = still loading, show nothing yet.
  const mine = useMemo(() => coveredAdmissionIds(patients, myBedCodes), [patients, myBedCodes]);
  const scoped = useMemo(
    () => (mine ? tasks.filter((t) => mine.has(String(t.admission_id))) : []),
    [tasks, mine]
  );

  // The window this list reports on: the dashboard's range when one is set,
  // otherwise today (EmarOverview's convention, shared by every view).
  const dayOf = (t) => String(t.scheduled_datetime || "").slice(0, 10);
  const hasRange = Boolean(dateFrom || dateTo);
  const today = todayStr();
  const windowStart = hasRange ? dateFrom || "0000-01-01" : today;
  const windowEnd = hasRange ? dateTo || "9999-12-31" : today;

  const windowTasks = useMemo(
    () => scoped.filter((t) => {
      const d = dayOf(t);
      return Boolean(d) && d >= windowStart && d <= windowEnd;
    }),
    [scoped, windowStart, windowEnd]
  );
  // Outstanding work scheduled before the window — the same backlog the
  // dashboard counts as overdue, listed here so it can actually be recorded.
  const overdueTasks = useMemo(
    () => scoped.filter((t) => isTaskActionable(t) && dayOf(t) && dayOf(t) < windowStart),
    [scoped, windowStart]
  );

  const taskTypes = useMemo(
    () => ["all", ...Array.from(new Set([...windowTasks, ...overdueTasks].map((t) => t.task_type).filter(Boolean)))],
    [windowTasks, overdueTasks]
  );

  const isDone = (t) => String(t.status).toLowerCase() === "completed";
  const matchesType = (t) => typeF === "all" || t.task_type === typeF;

  const filtered = windowTasks
    .filter(matchesType)
    .filter((t) => statusF === "all" || (statusF === "done" ? isDone(t) : !isDone(t)));
  // Overdue rows are outstanding by definition, so the status filter would only
  // ever empty this list — type still applies.
  const overdueFiltered = overdueTasks.filter(matchesType);

  const doneCount = windowTasks.filter(isDone).length;

  // A one-line label of what was ordered, so the row reads as the task not the row id.
  const taskLabel = (t) => {
    const med = t.order_details?.medication;
    if (med?.drug_name) return [med.drug_name, med.route, med.frequency].filter(Boolean).join(" · ");
    // task_type is the care plan's template name ("Task"), so it names nothing
    // on its own — prefer what the doctor wrote, then the form's name (DEFIPD082).
    return taskHeading(t, fulfillmentNames);
  };

  // Shared by the overdue list and the window list — the same row means the
  // same thing in both, and an overdue task stays recordable.
  const renderTask = (t) => {
    const key = t.id ?? `${t.care_plan_id}-${t.admission_id}`;
    const done = isDone(t);
    const actionable = isTaskActionable(t);
    const patient = patientByAdmission[String(t.admission_id)] || t.patient;
    const patientName = patient?.name || `Admission ${t.admission_id}`;
    return (
      <Paper
        key={key}
        variant="outlined"
        onClick={() => actionable && t.id != null && setActive(t)}
        sx={{ p: 1.5, borderRadius: 2, cursor: actionable ? "pointer" : "default", opacity: actionable || done ? 1 : 0.6 }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          {/* Status indicator, not a control — recording happens in the
              dialog the row opens (a disabled checkbox read as "broken"). */}
          {done
            ? <CheckCircleIcon color="success" />
            : <RadioButtonUncheckedIcon sx={{ color: "text.disabled" }} />}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, textDecoration: done ? "line-through" : "none" }}>
              {taskLabel(t)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {patientName}
              {t.time_slot_label ? ` · ${t.time_slot_label}` : ""}
              {" · "}{(t.scheduled_datetime || "").replace("T", " ").slice(0, 16)}
            </Typography>
          </Box>
          {t.prescription_received === false && !done && (
            <Chip size="small" color="warning" label="meds not received" sx={{ mr: 0.5 }} />
          )}
          <Chip
            size="small"
            variant="outlined"
            label={done ? "recorded" : (t.status || "pending")}
            color={done ? "success" : t.status === "in_progress" ? "warning" : "default"}
          />
        </Stack>
      </Paper>
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">My Tasks{hasRange ? "" : " · Today"}</Typography>
        <Typography variant="body2" color="text.secondary">{doneCount}/{windowTasks.length} completed · tap a task to record it.</Typography>
      </Box>
      <LinearProgress variant="determinate" value={windowTasks.length === 0 ? 0 : (doneCount / windowTasks.length) * 100} sx={{ mb: 2, height: 8, borderRadius: 1 }} />
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ flexWrap: "wrap" }}>
          <TextField select size="small" label="Type" value={typeF} onChange={(e) => setTypeF(e.target.value)} sx={{ minWidth: 160 }}>
            {taskTypes.map((p) => (<MenuItem key={p} value={p}>{p}</MenuItem>))}
          </TextField>
          <TextField select size="small" label="Status" value={statusF} onChange={(e) => setStatusF(e.target.value)} sx={{ minWidth: 160 }}>
            {["all", "pending", "done"].map((p) => (<MenuItem key={p} value={p}>{p}</MenuItem>))}
          </TextField>
        </Stack>
      </Paper>
      {overdueFiltered.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", mb: 1, flexWrap: "wrap" }}>
            <Typography variant="subtitle2" color="error">Overdue ({overdueFiltered.length})</Typography>
            <Typography variant="caption" color="text.secondary">
              still outstanding from before {hasRange ? "this date range" : "today"}
            </Typography>
          </Stack>
          <Stack spacing={1}>{overdueFiltered.map(renderTask)}</Stack>
        </Box>
      )}

      <Stack spacing={1}>
        {filtered.map(renderTask)}
        {filtered.length === 0 && (
          <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {overdueFiltered.length > 0
                ? `Nothing due ${hasRange ? "in this date range" : "today"} — the overdue work is listed above.`
                : "No tasks match the filter."}
            </Typography>
          </Paper>
        )}
      </Stack>

      {/* A medication dose is recorded through the administration sheet
          wherever it is tapped — two taps, no typing, and the withheld/refused
          outcomes on hand. Other nursing work keeps the generic form. */}
      {isMedicationTask(active) ? (
        <MedicationAdministrationSheet
          open={!!active}
          task={active}
          performedById={currentNurseId}
          onClose={() => setActive(null)}
          onRecorded={reload}
        />
      ) : (
        <TaskFulfillmentModal
          open={!!active}
          task={active}
          performedById={currentNurseId}
          onClose={() => setActive(null)}
          onFulfilled={reload}
        />
      )}
    </Box>
  );
}
